import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { formatRelativeUs, formatTimestampUs, shortId } from '../../lib/format';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import '../_shared/shared.css';
import './diagnostics.css';
import {
  cancelDiagnosticLock,
  createDiagnosticLock,
  effectiveLockStatus,
  listDiagnosticLocks,
  type DiagnosticLock,
  type DiagnosticLockStatus,
  type DiagnosticTargetType,
} from './diagnosticLocksApi';

// 狀態中文標籤(全中文導覽;英文 status 值只留在 API 層)
const STATUS_LABEL: Partial<Record<DiagnosticLock['status'], string>> = {
  active: '使用中',
  cancelled: '已取消',
  expired: '已到期',
};

const STATUS_TABS: { value: DiagnosticLockStatus; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'active', label: '使用中' },
  { value: 'cancelled', label: '已取消' },
  { value: 'expired', label: '已到期' },
];

export default function DiagnosticLocksPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const [locks, setLocks] = useState<DiagnosticLock[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [targetType, setTargetType] = useState<DiagnosticTargetType | ''>('');
  const [targetId, setTargetId] = useState('');
  const [status, setStatus] = useState<DiagnosticLockStatus>('active');
  const [createType, setCreateType] = useState<DiagnosticTargetType>('user');
  const [createTarget, setCreateTarget] = useState('');
  const [createTTL, setCreateTTL] = useState(60);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [stop, setStop] = useState<DiagnosticLock | null>(null);

  const load = useCallback(
    async (next?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const page = await listDiagnosticLocks({
          targetType,
          targetId: targetId.trim(),
          status,
          cursor: next ?? undefined,
          limit: 50,
        });
        setLocks((cur) => (next ? [...cur, ...page.locks] : page.locks));
        setCursor(page.nextCursor);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [targetType, targetId, status]
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  // 使用中鎖定摘要(純前端 filter 現有 locks 陣列,不多打 API)
  const activeLocks = useMemo(() => locks.filter((l) => effectiveLockStatus(l) === 'active'), [locks]);
  const soonestExpiry = useMemo(
    () => activeLocks.reduce<DiagnosticLock | null>((min, l) => (!min || l.expires_at < min.expires_at ? l : min), null),
    [activeLocks]
  );

  const canCreate = writable && createTarget.trim() !== '' && reason.trim() !== '' && caseId.trim() !== '';

  async function submitCreate() {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    try {
      await createDiagnosticLock({
        targetType: createType,
        targetId: createTarget.trim(),
        ttlMinutes: createTTL,
        reason: reason.trim(),
        caseId: caseId.trim(),
      });
      setCreateTarget('');
      setReason('');
      setCaseId('');
      await load(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function confirmStop() {
    if (!stop) return;
    setBusy(true);
    setError(null);
    try {
      await cancelDiagnosticLock(stop.lock_id, '從診斷鎖定頁停止', stop.case_id);
      setStop(null);
      await load(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="diag-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">診斷鎖定</h1>
          <p className="page-subtitle">使用中與歷史的診斷鎖定範圍</p>
        </div>
        <button className="btn-ghost btn-sm" onClick={() => void load(null)} disabled={loading}>
          重新整理
        </button>
      </div>

      {writable && (
        <section className="card card--primary">
          <h2 className="adm-card-title">建立鎖定</h2>
          <p className="diag-create-desc">
            建立鎖定會授權指定案件對此目標開啟診斷記錄存取,<strong>到期自動失效</strong>,無需手動清除。
          </p>
          <div className="diag-form">
            <label>
              <span>目標類型</span>
              <select value={createType} onChange={(e) => setCreateType(e.target.value as DiagnosticTargetType)}>
                <option value="user">使用者</option>
                <option value="device">裝置</option>
              </select>
            </label>
            <label className="diag-target">
              <span>目標 ID</span>
              <input
                value={createTarget}
                onChange={(e) => setCreateTarget(e.target.value)}
                placeholder={createType === 'user' ? 'u-us-...' : 'dev-...'}
              />
            </label>
            <label>
              <span>存續時間</span>
              <select value={createTTL} onChange={(e) => setCreateTTL(Number(e.target.value))}>
                <option value={15}>15 分鐘</option>
                <option value={60}>60 分鐘</option>
                <option value={240}>4 小時</option>
                <option value={1440}>24 小時</option>
              </select>
            </label>
            <label>
              <span>案件編號</span>
              <input value={caseId} onChange={(e) => setCaseId(e.target.value)} placeholder="CS-..." />
            </label>
            <label className="diag-reason">
              <span>原因</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="調查摘要" />
            </label>
            <button className="btn-primary btn-sm" onClick={() => void submitCreate()} disabled={!canCreate || busy}>
              建立鎖定
            </button>
          </div>
        </section>
      )}

      {!loading && error == null && activeLocks.length > 0 && (
        <div className="adm-summary-bar">
          <div className="adm-summary-bar-main">
            目前 <span className="text-brand">{activeLocks.length}</span> 筆使用中
            {soonestExpiry && (
              <span className="diag-summary-sub text-secondary">最快 {formatRelativeUs(soonestExpiry.expires_at)}到期</span>
            )}
          </div>
        </div>
      )}

      <div className="toolbar-panel">
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <select value={targetType} onChange={(e) => setTargetType(e.target.value as DiagnosticTargetType | '')}>
            <option value="">全部目標</option>
            <option value="user">使用者</option>
            <option value="device">裝置</option>
          </select>
          <input className="diag-filter-id" value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder="目標 ID" />
          <div className="segmented-control" role="tablist" aria-label="鎖定狀態">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                role="tab"
                aria-selected={status === t.value}
                className={status === t.value ? 'is-active' : ''}
                onClick={() => setStatus(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && locks.length === 0 && <LoadingState label="載入診斷鎖定…" />}
      {error != null && <ErrorState error={error} onRetry={() => void load(null)} />}

      {error == null && locks.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>鎖定目標</th>
                  <th>範圍</th>
                  <th>狀態</th>
                  <th>到期</th>
                  <th>案件</th>
                  <th>建立者</th>
                  <th>查詢</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {locks.map((lock) => (
                  <DiagnosticLockRow key={lock.lock_id} lock={lock} writable={writable} onStop={setStop} />
                ))}
              </tbody>
            </table>
          </div>
          {cursor && (
            <div className="adm-pager">
              <button className="btn-ghost btn-sm" onClick={() => void load(cursor)} disabled={loading}>
                載入更多
              </button>
            </div>
          )}
        </>
      )}

      {!loading && error == null && locks.length === 0 && (
        <EmptyState
          icon="🔍"
          title="沒有符合條件的診斷鎖定"
          detail={
            writable
              ? '調整上方篩選條件,或於上方「建立鎖定」授權新的診斷範圍。'
              : '調整上方篩選條件以查看其他狀態的鎖定。'
          }
        />
      )}

      {stop && (
        <ConfirmDialog
          title="停止診斷鎖定"
          body={
            <div className="diag-stop-summary">
              <div>停止後此診斷範圍立即失效,對應的記錄存取授權將被撤銷。</div>
              <dl className="diag-stop-facts">
                <div>
                  <dt>鎖定目標</dt>
                  <dd>
                    {stop.target_type === 'user' ? '使用者' : '裝置'}
                    <span className="mono">{stop.target_id}</span>
                  </dd>
                </div>
                <div>
                  <dt>範圍</dt>
                  <dd className="mono">{stop.diagnostic_scope_id}</dd>
                </div>
                <div>
                  <dt>案件</dt>
                  <dd className="mono">{stop.case_id || '—'}</dd>
                </div>
                <div>
                  <dt>到期</dt>
                  <dd title={formatTimestampUs(stop.expires_at)}>{formatRelativeUs(stop.expires_at)}</dd>
                </div>
              </dl>
            </div>
          }
          confirmLabel="停止"
          tone="warn"
          busy={busy}
          onCancel={() => setStop(null)}
          onConfirm={() => void confirmStop()}
        />
      )}
    </div>
  );
}

function DiagnosticLockRow({ lock, writable, onStop }: { lock: DiagnosticLock; writable: boolean; onStop: (lock: DiagnosticLock) => void }) {
  const eff = effectiveLockStatus(lock); // 過期即衍生為 expired,不顯示凍結的「使用中」
  const targetHref = lock.target_type === 'user' ? `/users/${encodeURIComponent(lock.target_id)}` : `/devices/${encodeURIComponent(lock.target_id)}`;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  function copyQuery() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(lock.log_query).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <tr>
      <td>
        <Link to={targetHref}>
          {lock.target_type === 'user' ? '使用者' : '裝置'}:{shortId(lock.target_id, 16, 5)}
        </Link>
      </td>
      <td className="mono" title={lock.diagnostic_scope_id}>
        {shortId(lock.diagnostic_scope_id, 15, 5)}
      </td>
      <td>
        <span className={`pill pill-dot ${eff === 'active' ? 'pill-good' : eff === 'expired' ? 'pill-warn' : 'pill-neutral'}`}>
          {STATUS_LABEL[eff] ?? eff}
        </span>
      </td>
      <td title={formatTimestampUs(lock.expires_at)}>{formatRelativeUs(lock.expires_at)}</td>
      <td className="mono">{lock.case_id || '—'}</td>
      <td className="mono">{lock.created_by ? shortId(lock.created_by, 14, 4) : '—'}</td>
      <td className="diag-logquery-cell">
        <div className="diag-logquery-controls">
          <button
            type="button"
            className="diag-logquery-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <span className="diag-logquery-caret" aria-hidden="true">▸</span>
            查詢
          </button>
          <button
            type="button"
            className="adm-copy-btn"
            title="複製查詢"
            aria-label="複製查詢"
            onClick={copyQuery}
          >
            {copied ? '已複製' : '⧉'}
          </button>
        </div>
        {expanded && <code className="adm-devlog-output">{lock.log_query}</code>}
      </td>
      <td className="diag-row-actions">
        {writable && eff === 'active' && (
          <button className="btn-ghost-warn btn-sm" onClick={() => onStop(lock)}>
            停止
          </button>
        )}
      </td>
    </tr>
  );
}
